/**
 * ErrorAlert component
 * Generic component for displaying error messages with consistent styling
 */

import React from "react";
import Typography, { ETypo, ETypoColor } from "@Front/Components/DesignSystem/Typography";
import Button, { EButtonVariant } from "@Front/Components/DesignSystem/Button";
import classes from "./classes.module.scss";

export type ErrorAlertVariant = "default" | "compact" | "inline";

export interface ErrorAlertProps {
	/**
	 * Error message to display
	 */
	error: string | null;

	/**
	 * Optional title (default: "⚠️ Erreur")
	 */
	title?: string;

	/**
	 * Optional retry action
	 */
	onRetry?: () => void;

	/**
	 * Visual variant
	 */
	variant?: ErrorAlertVariant;

	/**
	 * Custom className
	 */
	className?: string;
}

/**
 * Generic error alert component
 * Provides consistent error display across the application
 */
export function ErrorAlert({
	error,
	title = "⚠️ Erreur",
	onRetry,
	variant = "default",
	className,
}: ErrorAlertProps): React.ReactElement | null {
	if (!error) {
		return null;
	}

	const containerClassName = `${classes["error-alert"]} ${classes[`variant-${variant}`]} ${className || ""}`.trim();

	return (
		<div className={containerClassName}>
			{variant !== "inline" && (
				<Typography typo={ETypo.TEXT_LG_SEMIBOLD} color={ETypoColor.COLOR_ERROR_700}>
					{title}
				</Typography>
			)}
			<Typography
				typo={variant === "compact" ? ETypo.TEXT_XS_REGULAR : variant === "inline" ? ETypo.TEXT_SM_REGULAR : ETypo.TEXT_MD_REGULAR}
				color={variant === "inline" ? ETypoColor.COLOR_ERROR_700 : ETypoColor.COLOR_ERROR_700}>
				{error}
			</Typography>
			{onRetry && (
				<div className={classes["action"]}>
					<Button onClick={onRetry} variant={EButtonVariant.SECONDARY}>
						Réessayer
					</Button>
				</div>
			)}
		</div>
	);
}
